// Taşeron portalı — belge bağlama formlarının ALAN GRUBU durumu (GL #7; tasarım review §4.1). Elle
// yükleme (`UploadModal`), aksayan analizi bağlama (`StrandedLinkModal`) ve araç belgesi yükleme
// (`VehicleDocumentUploadModal`, tek grup) aynı durumu taşır:
//   - grup başına değer (`DocumentFieldsValue`; hiç dokunulmamış grup boş değerdir);
//   - tamamlık (seçilebilir tür + kural istiyorsa bitiş tarihi) ve link gövdesi (gizli tarih ASLA
//     gönderilmez);
//   - TÜR REDDİ GRUP BAŞINADIR: sunucu işlenen grubun türünü `INVALID`/`INACTIVE` ile reddederse
//     (§27.6) kayıt tazelenir, YALNIZ o grubun türü + tarihi temizlenir, hata o grubun seçicisinde
//     okunur, grup açılır ve odak seçici yeniden etkinleştiği ilk render'da oraya taşınır. Aynı
//     gövde yeniden GÖNDERİLMEZ;
//   - BAĞLANAN GRUP BİTMİŞTİR: `markLinked` sonrası grup "Kaydedildi" kartına döner (seçici yok) ve
//     tamamlık denetiminden düşer — bir tür reddinden sonra kayıt tazelenince bağlanmış bir grubun
//     pasifleşen türü `Kaydet`i kilitlemez (o grup zaten atlanacak).
// Ret durumu çevrilmiş METİN değil grup KİMLİĞİ olarak tutulur — dil değişince hata da değişir.

import {
  type ContractorDocumentLinkRequest,
  type DocumentEntity,
  type PersonnelDocumentLinkRequest,
  type VehicleDocumentUploadRequest,
  isSelectableDocumentType,
  isTypeFieldRejection,
  missingRequiredValidUntil,
  useDocumentTypes,
  validUntilToSend,
  withDocumentType,
} from '@isgportal/shared';
import { Check } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContractorDocumentFieldGroup } from '../DocumentUploadModal';
import { PersonnelDocumentFieldGroup } from '../PersonnelDocumentUploadModal';
import {
  type DocumentFieldsValue,
  EMPTY_DOCUMENT_FIELDS,
  documentFieldsComplete,
} from './DocumentTypeFields';

const NO_IDS: ReadonlySet<string> = new Set();

function withId(set: ReadonlySet<string>, id: string, present: boolean): ReadonlySet<string> {
  if (set.has(id) === present) {
    return set;
  }
  const next = new Set(set);
  if (present) {
    next.add(id);
  } else {
    next.delete(id);
  }
  return next;
}

/** Boş/whitespace dizeyi `null`'a indirger (opsiyonel alanlar). */
function nullable(value: string): string | null {
  return value.trim().length > 0 ? value.trim() : null;
}

/** §13.6 / §15.2 / §23.2 link gövdesi — üç ucun sözleşme tipinin HEPSİNE atanabilir (biri ayrışırsa
 * derleyici `linkBody`'de yakalar). */
type DocumentLinkBody = ContractorDocumentLinkRequest &
  PersonnelDocumentLinkRequest &
  VehicleDocumentUploadRequest;

export function useDocumentFieldGroups<E extends DocumentEntity>(entity: E) {
  const { t } = useTranslation();
  const registry = useDocumentTypes();
  const [values, setValues] = useState<Readonly<Record<string, DocumentFieldsValue>>>({});
  const [rejectedIds, setRejectedIds] = useState(NO_IDS);
  // Açık `<details>` grupları (ilk grup her zaman açıktır, burada tutulmaz).
  const [openIds, setOpenIds] = useState(NO_IDS);
  const [focusId, setFocusId] = useState<string | null>(null);
  const selects = useRef(new Map<string, HTMLSelectElement>());
  // Grup başına TEK callback ref — her render'da yenisi verilse React onu her render'da söküp yeniden
  // bağlardı (kuyruk ilerledikçe her tıkta).
  const selectRefs = useRef(new Map<string, (el: HTMLSelectElement | null) => void>());
  // Mutasyonun O AN bağladığı grup — bir tür reddi ona yazılır.
  const activeIdRef = useRef<string | null>(null);
  // Bağlanan gruplar. Ref = eşzamanlı gerçek (mutasyon döngüsü ve kapanışın silme hesabı aynı tıkta
  // okur); state = render (grup "Kaydedildi" kartına döner, `allComplete` onu atlar).
  const linkedRef = useRef(new Set<string>());
  const [linkedIds, setLinkedIds] = useState(NO_IDS);

  // Bağımlılıksız: her render'da ucuz bir denetim — seçici etkinleşince (mutasyon bitince) bir kez odak.
  useEffect(() => {
    if (focusId === null) {
      return;
    }
    const select = selects.current.get(focusId);
    if (!select || select.disabled) {
      return;
    }
    select.focus();
    setFocusId(null);
  });

  /** Yeni parti/açılış. `keepValues`: yalnız ret/açık/odak durumu sıfırlanır (girilen değerler kalır). */
  const reset = useCallback((options?: { keepValues?: boolean }) => {
    if (!options?.keepValues) {
      setValues({});
    }
    setRejectedIds(NO_IDS);
    setOpenIds(NO_IDS);
    setFocusId(null);
    activeIdRef.current = null;
    linkedRef.current = new Set();
    setLinkedIds(NO_IDS);
  }, []);

  function fieldsOf(id: string): DocumentFieldsValue {
    return values[id] ?? EMPTY_DOCUMENT_FIELDS;
  }

  function isComplete(id: string): boolean {
    return documentFieldsComplete(registry.types, entity, fieldsOf(id));
  }

  function setFields(id: string, next: DocumentFieldsValue) {
    // Kullanıcı türü değiştirdi → o grubun ret hatası düşer.
    if (next.type !== fieldsOf(id).type) {
      setRejectedIds((prev) => withId(prev, id, false));
    }
    setValues((prev) => ({ ...prev, [id]: next }));
  }

  function selectRef(id: string) {
    let ref = selectRefs.current.get(id);
    if (!ref) {
      ref = (el: HTMLSelectElement | null) => {
        if (el) {
          selects.current.set(id, el);
        } else {
          selects.current.delete(id);
        }
      };
      selectRefs.current.set(id, ref);
    }
    return ref;
  }

  return {
    entity,
    fieldsOf,
    isComplete,
    /**
     * Bağlanmamış tüm gruplar kaydedilebilir mi. Bağlanan grup atlanır (mutasyon onu da atlar);
     * boş liste: dolduracak alan yok → `true`.
     */
    allComplete: (ids: readonly string[]) => ids.every((id) => linkedIds.has(id) || isComplete(id)),
    /** Grup bu turda bağlandı — render için (state). */
    isLinked: (id: string) => linkedIds.has(id),
    /** Aynı — olay işleyicisi ve mutasyon döngüsü için (ref; aynı tıkta günceldir). */
    isLinkedNow: (id: string) => linkedRef.current.has(id),
    /** Bağlama başarılı: grup bir daha gönderilmez, silinmez, düzenlenmez. */
    markLinked(id: string) {
      linkedRef.current.add(id);
      setLinkedIds((prev) => withId(prev, id, true));
    },
    /** Seçilebilir türü olan ama zorunlu tarihi boş grup — kapalı bir grubun başlığında okunur. */
    needsDate: (id: string) => {
      const fields = fieldsOf(id);
      return (
        isSelectableDocumentType(registry.types, entity, fields.type) &&
        missingRequiredValidUntil(registry.types, entity, fields)
      );
    },
    setFields,
    /** Tür seçimi: gizlenen tarih boşaltılır (`withDocumentType`, D8). */
    setType: (id: string, code: string) =>
      setFields(id, withDocumentType(registry.types, entity, fieldsOf(id), code)),
    typeError: (id: string) => (rejectedIds.has(id) ? t('documentTypes.typeRejected') : undefined),
    isOpen: (id: string) => openIds.has(id),
    setOpen: (id: string, open: boolean) => setOpenIds((prev) => withId(prev, id, open)),
    selectRef,
    /** Grubun link gövdesi; grubu "işlenen" olarak işaretler (bir tür reddi ona yazılır). */
    linkBody(id: string, documentUuid: string): DocumentLinkBody {
      activeIdRef.current = id;
      const fields = fieldsOf(id);
      return {
        type: fields.type,
        documentUuid,
        validFrom: nullable(fields.validFrom),
        // `NoExpiry`'de alan gizlidir ve ASLA gönderilmez (D8).
        validUntil: validUntilToSend(registry.types, entity, fields),
        note: nullable(fields.note),
      };
    },
    /**
     * Mutasyonun hata yolu: hata işlenen grubun türüne yönelik bir retse (§27.6) kayıt tazelenir,
     * yalnız o grup temizlenir, hata + açık grup + odak — ve `true` döner. "İşlenen grup" son
     * `linkBody` çağrısıdır; arada düşen bir §8 yüklemesi buraya yanlış atfedilemez, çünkü §8.1/8.2
     * hataları tür alanı taşımaz (`fileName`/`contentType`/`size`).
     */
    handleLinkError(error: unknown): boolean {
      const id = activeIdRef.current;
      if (id === null || !isTypeFieldRejection(error)) {
        return false;
      }
      void registry.refetch();
      setValues((prev) => ({
        ...prev,
        [id]: { ...(prev[id] ?? EMPTY_DOCUMENT_FIELDS), type: '', validUntil: '' },
      }));
      setRejectedIds((prev) => withId(prev, id, true));
      setOpenIds((prev) => withId(prev, id, true));
      setFocusId(id);
      return true;
    },
    reset,
  };
}

export type DocumentFieldGroups<E extends DocumentEntity> = ReturnType<
  typeof useDocumentFieldGroups<E>
>;

export interface DocumentFieldGroupListProps {
  groups: readonly { id: string; name: string }[];
  fieldGroups: DocumentFieldGroups<'company' | 'personnel'>;
  disabled: boolean;
  /** Açık kartların (ilk grup + kaydedilen gruplar) başlığı; verilmezse kırpılmış dosya adı. */
  renderName?: (name: string) => ReactNode;
}

/**
 * Grupların listesi: ilk grup açık bir kart, kalanlar `<details>` (ret o grubu açar). Kapalı bir
 * grubun eksik ZORUNLU tarihi başlığında okunur — `Kaydet`in kapalı olma sebebi on grubun
 * yedincisinde gizli kalmasın. Bağlanan grup, sırası ne olursa olsun, seçicisiz bir "Kaydedildi"
 * kartıdır (ikon + metin; durum yalnız renkle taşınmaz).
 */
export function DocumentFieldGroupList({
  groups,
  fieldGroups,
  disabled,
  renderName,
}: DocumentFieldGroupListProps) {
  const { t } = useTranslation();
  const FieldGroup =
    fieldGroups.entity === 'personnel' ? PersonnelDocumentFieldGroup : ContractorDocumentFieldGroup;
  const header = (name: string) =>
    renderName ? (
      renderName(name)
    ) : (
      <p className="mb-3 truncate text-sm font-medium text-fg">{name}</p>
    );
  return (
    <div className="flex flex-col gap-3">
      {groups.map((group, index) => {
        if (fieldGroups.isLinked(group.id)) {
          return (
            <div key={group.id} className="rounded-md border border-border p-3">
              {header(group.name)}
              <p className="flex items-center gap-1.5 text-sm text-fg">
                <Check className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                {t('documents.field.saved')}
              </p>
            </div>
          );
        }
        const body = (
          <FieldGroup
            idPrefix={group.id}
            value={fieldGroups.fieldsOf(group.id)}
            onChange={(next) => fieldGroups.setFields(group.id, next)}
            disabled={disabled}
            typeError={fieldGroups.typeError(group.id)}
            typeSelectRef={fieldGroups.selectRef(group.id)}
          />
        );
        if (index === 0) {
          return (
            <div key={group.id} className="rounded-md border border-border p-3">
              {header(group.name)}
              {body}
            </div>
          );
        }
        return (
          <details
            key={group.id}
            open={fieldGroups.isOpen(group.id)}
            onToggle={(e) => fieldGroups.setOpen(group.id, e.currentTarget.open)}
            className="rounded-md border border-border p-3"
          >
            <summary className="cursor-pointer break-words text-sm font-medium text-fg">
              {group.name}
              {fieldGroups.needsDate(group.id) ? (
                <span className="mt-0.5 block text-xs font-normal text-muted">
                  {t('documents.field.validUntilRequired')}
                </span>
              ) : null}
            </summary>
            <div className="mt-3">{body}</div>
          </details>
        );
      })}
    </div>
  );
}
